export const DEFAULT_ACCEPTED_DOCUMENTS = [
  {
    title: 'Driving License',
    type: 'drivinglicense',
    sub: 'Document that permits you to drive a vehicle',
  },
  {
    title: 'National ID',
    type: 'nationalidcard',
    sub: "National ID that verifies a person's identity",
  },
  {
    title: 'Passport',
    type: 'passport',
    sub: 'Travel document issued by a government',
  },
];

// null means use the existing tenant defaults; [] intentionally accepts no types.
export const resolveAcceptedDocuments = (value) => {
  if (!Array.isArray(value) || !value.every((entry) =>
    entry && typeof entry.type === 'string' && entry.type.trim() &&
    typeof entry.title === 'string' && entry.title.trim() &&
    (entry.sub === undefined || typeof entry.sub === 'string') &&
    (entry.excludeFromUpload === undefined || typeof entry.excludeFromUpload === 'boolean') &&
    (entry.isSupporting === undefined || typeof entry.isSupporting === 'boolean')
  )) return null;
  const documents = new Map();
  value.forEach((entry) => documents.set(entry.type.trim(), {
    type: entry.type.trim(), title: entry.title.trim(), sub: entry.sub ?? '',
    excludeFromUpload: entry.excludeFromUpload ?? false,
    isSupporting: entry.isSupporting ?? false,
  }));
  return [...documents.values()];
};

export const selectAcceptedDocuments = (state) => state.app.acceptedDocuments ?? DEFAULT_ACCEPTED_DOCUMENTS;
export const isDocumentUploadExcluded = (type, documents) =>
  documents.some((document) => document.type === type && document.excludeFromUpload === true);
export const isSupportingDocument = (type, documents) =>
  documents.some((document) => document.type === type && document.isSupporting === true);
export const getPrimaryUploadDocuments = (documents) =>
  getUploadDocuments(documents).filter((document) => !isSupportingDocument(document.type, documents));
export const getServiceUploadTypes = (types, documents) =>
  types.filter((type) => !isSupportingDocument(type.dsscId, documents) && !isDocumentUploadExcluded(type.dsscId, documents));
export const getUploadDocuments = (documents) =>
  documents.filter((document) => !isDocumentUploadExcluded(document.type, documents));
export const isUploadSelectionAvailable = (type, options) =>
  !!type && type !== 'selecttype' && options.some((option) => option.value === type);

// Additional upload types remain separate from the primary KYC allowlist.
export const getDocumentUploadOptions = ({ acceptedDocuments, idTypes = [], serviceIdTypes,
  documents = [], additionalDocumentTypes = '' }) => {
  const baseOptions = serviceIdTypes
    ? getServiceUploadTypes(serviceIdTypes, acceptedDocuments).map((entry) => ({ value: entry.dsscId, display: entry.name }))
    : [
      ...acceptedDocuments.map((entry) => ({ value: entry.type, display: entry.title })),
      ...idTypes
        .filter((entry) => entry.dsscId !== 'others' && entry.dsscId !== 'default' &&
          !acceptedDocuments.some((accepted) => accepted.type === entry.dsscId))
        .map((entry) => ({ value: entry.dsscId, display: entry.name })),
    ];
  const hasDocuments = !!documents?.length;
  const additionalOptions = additionalDocumentTypes.split(',').flatMap((entry) => {
    const [withoutDocuments, type, title] = entry.split('|').map((value) => value.trim());
    const available = (withoutDocuments === 'true' && !hasDocuments) || (withoutDocuments === 'false' && hasDocuments);
    return available && type && title ? [{ value: type.toLowerCase(), display: title }] : [];
  });
  const options = [...baseOptions, ...additionalOptions].filter((entry, index, entries) =>
    entry.value && entry.value !== 'selecttype' && !isDocumentUploadExcluded(entry.value, acceptedDocuments) &&
    (!serviceIdTypes || (!isSupportingDocument(entry.value, acceptedDocuments) && serviceIdTypes.some((type) => type.dsscId === entry.value))) &&
    entries.findIndex((candidate) => candidate.value === entry.value) === index,
  );
  return [{ value: 'selecttype', display: 'Select Type' }, ...options];
};
